import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, resolveAssetUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useContent } from "../contexts/ContentContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Upload, LogOut, Save, Plus, Trash2, Image as ImageIcon, FileText } from "lucide-react";

const LANGS = ["ca", "es", "en"];
const LANG_LABEL = { ca: "Català", es: "Español", en: "English" };

function Section({ title, children }) {
    return (
        <section className="bg-white rounded-xl border border-brand-navy/10 p-6 md:p-8 mb-6">
            <h3 className="font-display text-2xl text-brand-navy mb-6">{title}</h3>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-4">
            <span className="text-xs uppercase tracking-[0.18em] text-brand-navy/70 block mb-2">{label}</span>
            {children}
        </label>
    );
}

function Input({ value, onChange, ...rest }) {
    return (
        <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/20 focus:border-brand-terracotta focus:outline-none focus:ring-2 focus:ring-brand-terracotta/20 text-sm"
            {...rest}
        />
    );
}

function TextArea({ value, onChange, rows = 4, ...rest }) {
    return (
        <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/20 focus:border-brand-terracotta focus:outline-none focus:ring-2 focus:ring-brand-terracotta/20 text-sm leading-relaxed"
            {...rest}
        />
    );
}

async function uploadFile(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
}

export default function Admin() {
    const { user, booting, logout } = useAuth();
    const { content, refetch } = useContent();
    const [local, setLocal] = useState(null);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!booting && !user) navigate("/admin/login");
    }, [booting, user, navigate]);

    useEffect(() => {
        if (content) setLocal(JSON.parse(JSON.stringify(content)));
    }, [content]);

    if (booting || !user || !local) {
        return <div className="min-h-screen flex items-center justify-center font-display text-3xl text-brand-navy">Zarautz</div>;
    }

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/content", local);
            toast.success("Canvis desats");
            await refetch();
        } catch (e) {
            toast.error("Error en desar");
        } finally {
            setSaving(false);
        }
    };

    const onLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    const updateT = (lang, path, value) => {
        setLocal((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let ref = next.translations[lang];
            for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]] = ref[keys[i]] || {};
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const updateField = (path, value) => {
        setLocal((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let ref = next;
            for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]] = ref[keys[i]] || {};
            ref[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const addFaqItem = (lang) => {
        setLocal((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const items = next.translations[lang].faq?.items || [];
            items.push({ q: "", a: "" });
            next.translations[lang].faq = { ...(next.translations[lang].faq || {}), items };
            return next;
        });
    };

    const removeFaqItem = (lang, idx) => {
        setLocal((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            next.translations[lang].faq.items.splice(idx, 1);
            return next;
        });
    };

    const handleUpload = async (kind, lang, file) => {
        if (!file) return;
        try {
            toast.info("Pujant...");
            const url = await uploadFile(file);
            if (kind === "menu") updateField(`menu_pdf.${lang}`, url);
            else if (kind === "groups") updateField(`groups_pdf.${lang}`, url);
            else if (kind === "wine") updateField(`wine_pdf.${lang}`, url);
            else if (kind === "hero") updateField("hero_image", url);
            toast.success("Pujat");
        } catch (e) {
            toast.error("Error pujant l'arxiu");
        }
    };

    const addGalleryImage = async (file) => {
        if (!file) return;
        try {
            toast.info("Pujant...");
            const url = await uploadFile(file);
            setLocal((prev) => {
                const next = JSON.parse(JSON.stringify(prev));
                next.gallery_images = [...(next.gallery_images || []), { url, alt: "Zarautz" }];
                return next;
            });
            toast.success("Imatge afegida");
        } catch {
            toast.error("Error");
        }
    };

    const removeGalleryImage = (idx) => {
        setLocal((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            next.gallery_images.splice(idx, 1);
            return next;
        });
    };

    return (
        <div className="min-h-screen bg-brand-cream" data-testid="admin-page">
            <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-brand-navy/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="font-display text-2xl text-brand-navy">Zarautz · Admin</Link>
                    <div className="flex items-center gap-3">
                        <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-4 text-xs" data-testid="admin-save-btn">
                            <Save size={14} /> {saving ? "Desant..." : "Desar canvis"}
                        </button>
                        <button onClick={onLogout} className="text-xs uppercase tracking-wider text-brand-navy/70 hover:text-brand-terracotta flex items-center gap-1" data-testid="admin-logout-btn">
                            <LogOut size={14} /> Sortir
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                <Section title="Enllaç de reserves (Covermanager)">
                    <Field label="URL de Covermanager">
                        <Input value={local.covermanager_url} onChange={(v) => updateField("covermanager_url", v)} data-testid="admin-covermanager-url" />
                    </Field>
                </Section>

                <Section title="Imatge principal (Hero)">
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                        {local.hero_image && (
                            <img src={resolveAssetUrl(local.hero_image)} alt="Hero" className="w-full md:w-64 aspect-[4/3] object-cover rounded-lg" />
                        )}
                        <div className="flex-1">
                            <Field label="URL de la imatge">
                                <Input value={local.hero_image} onChange={(v) => updateField("hero_image", v)} data-testid="admin-hero-url" />
                            </Field>
                            <label className="btn-outline !py-2 !px-4 text-xs cursor-pointer inline-flex" data-testid="admin-hero-upload">
                                <Upload size={14} /> Pujar imatge
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload("hero", null, e.target.files?.[0])} />
                            </label>
                        </div>
                    </div>
                </Section>

                <Section title="Menús en PDF">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {LANGS.map((l) => (
                            <div key={l} className="border border-brand-navy/10 rounded-lg p-4">
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-blue mb-3">{LANG_LABEL[l]}</div>
                                <Field label="Carta (PDF)">
                                    <Input value={local.menu_pdf?.[l]} onChange={(v) => updateField(`menu_pdf.${l}`, v)} />
                                </Field>
                                <label className="btn-outline !py-1.5 !px-3 text-[10px] cursor-pointer inline-flex mb-4">
                                    <FileText size={12} /> Pujar PDF
                                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload("menu", l, e.target.files?.[0])} data-testid={`admin-upload-menu-${l}`} />
                                </label>
                                <Field label="Menús de grup (PDF)">
                                    <Input value={local.groups_pdf?.[l]} onChange={(v) => updateField(`groups_pdf.${l}`, v)} />
                                </Field>
                                <label className="btn-outline !py-1.5 !px-3 text-[10px] cursor-pointer inline-flex mb-4">
                                    <FileText size={12} /> Pujar PDF
                                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload("groups", l, e.target.files?.[0])} />
                                </label>
                                <Field label="Carta de vins (PDF)">
                                    <Input value={local.wine_pdf?.[l]} onChange={(v) => updateField(`wine_pdf.${l}`, v)} />
                                </Field>
                                <label className="btn-outline !py-1.5 !px-3 text-[10px] cursor-pointer inline-flex">
                                    <FileText size={12} /> Pujar PDF
                                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload("wine", l, e.target.files?.[0])} />
                                </label>
                            </div>
                        ))}
                    </div>
                </Section>

                <Section title="Galeria">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        {(local.gallery_images || []).map((img, i) => (
                            <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-brand-navy/10">
                                <img src={resolveAssetUrl(img.url)} alt={img.alt} className="w-full h-full object-cover" />
                                <button onClick={() => removeGalleryImage(i)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`admin-gallery-remove-${i}`}>
                                    <Trash2 size={14} className="text-red-600" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <label className="btn-outline cursor-pointer inline-flex !py-2 !px-4 text-xs" data-testid="admin-gallery-add">
                        <ImageIcon size={14} /> Afegir imatge
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => addGalleryImage(e.target.files?.[0])} />
                    </label>
                </Section>

                <Section title="Informació de contacte">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Adreça"><Input value={local.contact_info?.address} onChange={(v) => updateField("contact_info.address", v)} /></Field>
                        <Field label="Telèfon"><Input value={local.contact_info?.phone} onChange={(v) => updateField("contact_info.phone", v)} /></Field>
                        <Field label="Email"><Input value={local.contact_info?.email} onChange={(v) => updateField("contact_info.email", v)} /></Field>
                        <Field label="Google Maps embed URL"><Input value={local.contact_info?.map_embed} onChange={(v) => updateField("contact_info.map_embed", v)} /></Field>
                        <Field label="Instagram"><Input value={local.contact_info?.instagram} onChange={(v) => updateField("contact_info.instagram", v)} /></Field>
                        <Field label="Facebook"><Input value={local.contact_info?.facebook} onChange={(v) => updateField("contact_info.facebook", v)} /></Field>
                    </div>
                </Section>

                <Section title="Horaris">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
                            <Field key={d} label={d.toUpperCase()}>
                                <Input value={local.hours?.[d]} onChange={(v) => updateField(`hours.${d}`, v)} />
                            </Field>
                        ))}
                    </div>
                </Section>

                <Section title="Textos per idioma">
                    <Tabs defaultValue="ca">
                        <TabsList className="mb-6" data-testid="admin-lang-tabs">
                            {LANGS.map((l) => (
                                <TabsTrigger key={l} value={l} data-testid={`admin-tab-${l}`}>{LANG_LABEL[l]}</TabsTrigger>
                            ))}
                        </TabsList>
                        {LANGS.map((l) => {
                            const tr = local.translations?.[l] || {};
                            return (
                                <TabsContent key={l} value={l} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Field label="Hero - Kicker"><Input value={tr.hero?.kicker} onChange={(v) => updateT(l, "hero.kicker", v)} /></Field>
                                        <Field label="Hero - Títol"><Input value={tr.hero?.title} onChange={(v) => updateT(l, "hero.title", v)} /></Field>
                                        <Field label="Hero - Subtítol"><TextArea value={tr.hero?.subtitle} onChange={(v) => updateT(l, "hero.subtitle", v)} /></Field>
                                        <Field label="Hero - CTA"><Input value={tr.hero?.cta} onChange={(v) => updateT(l, "hero.cta", v)} /></Field>

                                        <Field label="Sobre - Títol"><Input value={tr.about?.title} onChange={(v) => updateT(l, "about.title", v)} /></Field>
                                        <Field label="Sobre - Text"><TextArea rows={6} value={tr.about?.body} onChange={(v) => updateT(l, "about.body", v)} /></Field>

                                        <Field label="Menú - Títol"><Input value={tr.menu?.title} onChange={(v) => updateT(l, "menu.title", v)} /></Field>
                                        <Field label="Menú - Subtítol"><TextArea value={tr.menu?.subtitle} onChange={(v) => updateT(l, "menu.subtitle", v)} /></Field>

                                        <Field label="Menú - Botó Carta"><Input value={tr.menu?.download_carta} onChange={(v) => updateT(l, "menu.download_carta", v)} placeholder="Descarregar la carta" /></Field>
                                        <Field label="Menú - Botó Grups"><Input value={tr.menu?.download_menus} onChange={(v) => updateT(l, "menu.download_menus", v)} placeholder="Menús de grup" /></Field>
                                        <Field label="Menú - Botó Vins"><Input value={tr.menu?.download_wine} onChange={(v) => updateT(l, "menu.download_wine", v)} placeholder="Carta de vins" /></Field>

                                        <Field label="Galeria - Títol"><Input value={tr.gallery?.title} onChange={(v) => updateT(l, "gallery.title", v)} /></Field>
                                        <Field label="Contacte - Títol"><Input value={tr.contact?.title} onChange={(v) => updateT(l, "contact.title", v)} /></Field>
                                    </div>

                                    <div>
                                        <h4 className="font-display text-xl text-brand-navy mt-4 mb-3">FAQ</h4>
                                        <Field label="Títol secció"><Input value={tr.faq?.title} onChange={(v) => updateT(l, "faq.title", v)} /></Field>
                                        <div className="space-y-4">
                                            {(tr.faq?.items || []).map((item, i) => (
                                                <div key={i} className="border border-brand-navy/10 rounded-lg p-4 bg-brand-cream/30">
                                                    <div className="flex justify-between items-start gap-2 mb-2">
                                                        <span className="text-[10px] uppercase tracking-[0.2em] text-brand-blue">#{i + 1}</span>
                                                        <button onClick={() => removeFaqItem(l, i)} className="text-red-600 hover:text-red-700" data-testid={`admin-faq-remove-${l}-${i}`}>
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                    <Field label="Pregunta">
                                                        <Input value={item.q} onChange={(v) => {
                                                            const items = [...tr.faq.items];
                                                            items[i] = { ...items[i], q: v };
                                                            updateT(l, "faq.items", items);
                                                        }} />
                                                    </Field>
                                                    <Field label="Resposta">
                                                        <TextArea value={item.a} onChange={(v) => {
                                                            const items = [...tr.faq.items];
                                                            items[i] = { ...items[i], a: v };
                                                            updateT(l, "faq.items", items);
                                                        }} />
                                                    </Field>
                                                </div>
                                            ))}
                                        </div>
                                        <button onClick={() => addFaqItem(l)} className="btn-outline !py-2 !px-4 text-xs mt-4" data-testid={`admin-faq-add-${l}`}>
                                            <Plus size={14} /> Afegir pregunta
                                        </button>
                                    </div>
                                </TabsContent>
                            );
                        })}
                    </Tabs>
                </Section>

                <div className="flex justify-end pt-4 pb-20">
                    <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-save-btn-bottom">
                        <Save size={16} /> {saving ? "Desant..." : "Desar canvis"}
                    </button>
                </div>
            </main>
        </div>
    );
}
